import { useCallback, useRef, useState, useEffect } from 'react'

import { Paper, InputBase, List, ListItem, ListItemText, CircularProgress, Typography } from '@mui/material'

import IconButton from '@mui/material/IconButton'

import {
  Search as SearchIcon,
  HighlightOff as XIcon,
  // SearchOff as XSearchIcon
} from '@mui/icons-material'

import { createLSFeatureFlag } from '../../util/storage'
import { System } from '../../Reusable'
import { queryKnownMatchMedia } from '../../adapters/windowUtils'
import { withPreventDefault } from '../../adapters/Eventing'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import {
  FocusType,
  searchActions,
  SearchMsg,
  SearchPayload,
  // SuggestionsLoadRequestedMsg, // Removed
  // ClearSuggestionsMsg, // Removed
} from '../../store/search-slice'
import { getSearchCmdDispatch } from '../../adapters/FeedAdapter'

export const contentSearchFeatureFlagKey = 'allowContentSearch'

const contentSearchFeatureFlag = createLSFeatureFlag(contentSearchFeatureFlagKey, true, true)

export type ContentSearchProps = {
  // focus: System.PropWrapper<boolean>
  cardClassName: string
  categories?: string[]
  // onSearch: SearchInvokeAsync
}

// Debounce utility - can be removed if not used elsewhere after this revert
// const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
//   let timeout: ReturnType<typeof setTimeout> | null = null
//
//   const debounced = (...args: Parameters<F>) => {
//     if (timeout !== null) {
//       clearTimeout(timeout)
//       timeout = null
//     }
//     timeout = setTimeout(() => func(...args), waitFor)
//   }
//
//   return debounced as (...args: Parameters<F>) => ReturnType<F>
// }

// nice to have: server flag forcing content search off
export const ContentSearch = ({ cardClassName }: ContentSearchProps) => {
  const dispatch = useDispatch()
  const cmdDispatch = getSearchCmdDispatch(dispatch)

  const userSearchState = useSelector((state: RootState) => state.search)
  const committedSearchTermFromRedux = userSearchState.currentShownKey
  // const suggestionItems = useSelector((state: RootState) => state.search.suggestionItems) // Removed
  // const isLoadingSuggestions = useSelector((state: RootState) => state.search.isLoadingSuggestions) // Removed

  const [searchText, setSearchText] = useState<string>(() => committedSearchTermFromRedux ?? '')

  // console.log(
  //   'ContentSearch render. Local searchText:',
  //   searchText,
  //   'Redux currentShownKey:',
  //   committedSearchTermFromRedux,
  //   'searchFocused:',
  //   userSearchState.searchFocused
  // )

  useEffect(() => {
    const currentCommittedTermInRedux = committedSearchTermFromRedux ?? ''
    if (currentCommittedTermInRedux !== searchText) {
      setSearchText(currentCommittedTermInRedux)
    }
  }, [committedSearchTermFromRedux])

  const input = useRef<HTMLInputElement | undefined>(undefined)
  const searchContainerRef = useRef<HTMLFormElement>(null)
  let isSmallDevice = queryKnownMatchMedia('isSmallDevice') ?? false

  let onSearchClick = async (textToSearch: string) => {
    // console.log('onSearchClick searching for:', textToSearch)
    let msg: SearchMsg = {
      type: 'PostsLoadRequestedMsg',
      searchInput: textToSearch,
    }
    dispatch(searchActions.update({ msg, cmdDispatch }))
    // dispatch(searchActions.update({ msg: { type: 'ClearSuggestionsMsg' } as ClearSuggestionsMsg, cmdDispatch })) // Removed
  }

  let updateFocusCb = useCallback(
    (userRequest: boolean, focusType: FocusType) => {
      let msg: SearchMsg = {
        type: 'RefocusRequestedMsg',
        key: committedSearchTermFromRedux,
        userRequest,
        focusType,
      }
      dispatch(searchActions.update({ msg, cmdDispatch }))
    },
    [committedSearchTermFromRedux, dispatch, cmdDispatch]
  )

  // Removed debouncedLoadSuggestions and related useEffect (Effect 2)

  let showContentSearch = contentSearchFeatureFlag.getter()
  if (showContentSearch !== true) {
    return <div />
  }

  let hasValidSearchForSubmit = searchText && searchText.length > 2
  let lastCommittedSearch = userSearchState.postItemsSearch?.key

  let searchSubmitDisabled: boolean =
    userSearchState.inFlightSearch !== undefined || // forSuggestions part removed
    lastCommittedSearch === searchText ||
    !hasValidSearchForSubmit

  let onClearClick = () => {
    setSearchText('')
    let msg: SearchMsg = {
      type: 'UserSearchClearMsg',
    }
    dispatch(searchActions.update({ msg, cmdDispatch }))
    // dispatch(searchActions.update({ msg: { type: 'ClearSuggestionsMsg' } as ClearSuggestionsMsg, cmdDispatch })) // Removed
  }

  // console.log(
  //   'ContentSearch values:',
  //   JSON.stringify({
  //     localSearchText: searchText,
  //     searchSubmitDisabled,
  //     isFocus: userSearchState.searchFocused,
  //     awaitingMainSearchResult: userSearchState.inFlightSearch !== undefined, // forSuggestions part removed
  //     // isLoadingSuggestions, // Removed
  //     committedSearchTermFromRedux,
  //   })
  // )

  let compensateForLongSearch: boolean = (isSmallDevice && searchText && searchText.length > 30) || false
  let maxPaperWidth = userSearchState.searchFocused ? (isSmallDevice ? 320 : 505) : 350

  if (!userSearchState.searchFocused)
    return (
      <IconButton
        type="button"
        sx={{ p: '10px' }}
        style={{ color: '#fff' }}
        aria-label="search"
        onClick={() => updateFocusCb(true, 'WantsFocus')}
      >
        <SearchIcon />
      </IconButton>
    )

  return (
    <div style={{ position: 'relative' }}>
      <Paper
        ref={searchContainerRef}
        component={'form'}
        onSubmit={withPreventDefault(() => (!searchSubmitDisabled ? onSearchClick(searchText) : {}))}
        elevation={14}
        className={cardClassName}
        sx={{
          p: '2px 4px',
          height: userSearchState.searchFocused ? '46px' : isSmallDevice ? undefined : '40px',
          display: 'flex',
          alignItems: 'center',
          width: maxPaperWidth,
          marginTop: '5px',
          marginBottom: '5px',
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Search OneView Home"
          tabIndex={1}
          inputProps={{ 'aria-label': 'search oneView home' }}
          fullWidth={compensateForLongSearch}
          inputRef={input}
          value={searchText}
          autoFocus
          onFocus={() => updateFocusCb(true, 'WantsFocus')}
          onChange={e => setSearchText(e.target.value)}
          // disabled={isLoadingSuggestions /* Disable input while loading suggestions */} // Removed
        />
        {/* {isLoadingSuggestions && <CircularProgress size={24} sx={{ p: '10px' }} />} // Removed */}
        <IconButton
          type="button"
          sx={{ p: '10px' }}
          aria-label="search"
          disabled={searchSubmitDisabled /* || isLoadingSuggestions */} // isLoadingSuggestions part removed
          tabIndex={2}
          onClick={() => onSearchClick(searchText)}
        >
          <SearchIcon />
        </IconButton>
        <IconButton
          title={'Exit Search Mode'}
          tabIndex={3}
          type="button"
          sx={{ p: '10px' }}
          onClick={() => {
            onClearClick()
            updateFocusCb(true, 'WantsUnfocus')
          }}
          aria-label="search"
        >
          <XIcon />
        </IconButton>
      </Paper>
      {/* Suggestion rendering logic removed */}
    </div>
  )
}
